import { z } from 'zod';
// Single source of truth for the privileged-API permission taxonomy (auth
// layer, token/OAuth minting, settings UI, OpenAPI). See .ai/plans/api-tokens.
export const API_SCOPES = [
    'events:write',
    'identities:write',
    'groups:write',
    'consent:write',
    'companies:write',
    'actions:write',
    'events:read',
    'analytics:read',
    'revenue:read',
    'identities:read',
    'actions:read',
    'privacy_requests:read',
    'privacy_requests:write',
    'embeds:write',
    'projects:read',
    'organization:read',
];
// Scopes that are a plan feature when used by a MACHINE credential (API token or
// OAuth client). Unmapped scopes (ingest, privacy requests) work on every plan.
export const SCOPE_FEATURE = {
    'companies:write': 'apiAccess',
    'actions:write': 'apiAccess',
    'actions:read': 'apiAccess',
    'events:read': 'apiAccess',
    'analytics:read': 'apiAccess',
    'revenue:read': 'apiAccess',
    'identities:read': 'apiAccess',
    'projects:read': 'apiAccess',
    'organization:read': 'apiAccess',
    'embeds:write': 'apiAccess',
};
export const apiScopeSchema = z.enum(API_SCOPES);
// The only scopes an MCP client may request: those backed by an MCP tool. Ingest,
// embed, and privacy-request scopes belong to machine credentials, never to an assistant.
export const MCP_SCOPES = [
    'analytics:read',
    'events:read',
    'revenue:read',
    'identities:read',
    'projects:read',
    'companies:write',
    'actions:write',
    'actions:read',
];
// Grouped for the token-creation UI (grouped checkboxes) and the docs scope
// table. Every scope in API_SCOPES appears exactly once across these groups.
export const SCOPE_GROUPS = [
    {
        label: 'Ingest',
        scopes: [
            {
                scope: 'events:write',
                label: 'Write Events',
                description: 'Send events server-side via /track.',
            },
            {
                scope: 'identities:write',
                label: 'Write Identities',
                description: 'Identify users server-side via /identify.',
            },
            {
                scope: 'groups:write',
                label: 'Write Groups',
                description: 'Associate users with groups via /group.',
            },
            {
                scope: 'consent:write',
                label: 'Write Consent',
                description: 'Record consent server-side via /consent.',
            },
        ],
    },
    {
        label: 'Read Data',
        scopes: [
            {
                scope: 'events:read',
                label: 'Read Events',
                description: 'Search raw events via /search-events.',
            },
            {
                scope: 'analytics:read',
                label: 'Read Analytics',
                description: 'Read analytics counts, aggregates, and metrics.',
            },
            {
                scope: 'revenue:read',
                label: 'Read Revenue',
                description: 'Read revenue metrics, attribution, and cohorts.',
            },
            {
                scope: 'identities:read',
                label: 'Read Identities',
                description: 'Read identity profiles, sessions, and activity. Includes personal data.',
            },
            {
                scope: 'actions:read',
                label: 'Read Actions',
                description: 'List saved actions and the action catalog.',
            },
        ],
    },
    {
        label: 'Privacy Requests',
        scopes: [
            {
                scope: 'privacy_requests:read',
                label: 'Read Privacy Requests',
                description: 'Read the status and results of privacy requests (deletion and export, also called DSR/DSAR).',
            },
            {
                scope: 'privacy_requests:write',
                label: 'File Privacy Requests',
                description: 'File deletion and export (DSAR) privacy requests for the organization.',
            },
        ],
    },
    {
        label: 'Embeds',
        scopes: [
            {
                scope: 'embeds:write',
                label: 'Write Embeds',
                description: 'Mint embed one-time tokens via /embed/ott.',
            },
        ],
    },
    {
        label: 'Organization',
        scopes: [
            {
                scope: 'projects:read',
                label: 'Read Projects',
                description: "List the organization's projects.",
            },
            {
                scope: 'organization:read',
                label: 'Read Organization',
                description: 'Read organization members and settings metadata.',
            },
        ],
    },
    {
        label: 'Configuration',
        scopes: [
            {
                scope: 'companies:write',
                label: 'Manage Companies & Target Accounts',
                description: 'Create Target Accounts and update company profiles.',
            },
            {
                scope: 'actions:write',
                label: 'Create & Run Actions',
                description: 'Create saved actions and run them (running may spend credits).',
            },
        ],
    },
];
export const API_SCOPE_BY_KEY = Object.fromEntries(SCOPE_GROUPS.flatMap((group) => group.scopes.map((scope) => [scope.scope, scope])));
export const resourceScopeSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('organization') }),
    z.object({
        type: z.literal('projects'),
        projectIds: z.array(z.string().min(1)).min(1),
    }),
]);
export const createApiTokenSchema = z.object({
    name: z.string().trim().min(1).max(120),
    resourceScope: resourceScopeSchema,
    scopes: z.array(apiScopeSchema).min(1),
    // Seconds until expiry, or null for a non-expiring token. Capped at ~400 days.
    expiresIn: z
        .number()
        .int()
        .min(1)
        .max(400 * 24 * 60 * 60)
        .nullable()
        .default(null),
});
// A legacy `sk_` key's frozen abilities: what it can do today, minus every org
// scope, so normalizing it onto the new layer never widens its reach.
export const LEGACY_SK_SCOPES = [
    'events:write',
    'identities:write',
    'groups:write',
    'consent:write',
    'events:read',
    'analytics:read',
    'embeds:write',
];
export const createOAuthClientSchema = z.object({
    name: z.string().trim().min(1).max(100),
    scopes: z.array(apiScopeSchema).min(1),
});
//# sourceMappingURL=api-tokens.js.map